import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Mail, ArrowLeft, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import api from '@/services/api';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const forgotPasswordMutation = useMutation({
        mutationFn: async (data) => {
            const response = await api.post('/auth/forgot-password', data);
            return response.data;
        },
        onSuccess: () => {
            setSubmitted(true);
            setError('');
        },
        onError: (error) => {
            // Check for rate limiting
            if (error.response?.status === 429) {
                setError('Too many password reset requests. Please try again later.');
            } else {
                // Generic error message for security
                setError('Failed to process request. Please try again.');
            }
        }
    });

    const validateEmail = (email) => {
        return /\S+@\S+\.\S+/.test(email);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!email) {
            setError('Email is required');
            return;
        }

        if (!validateEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }

        forgotPasswordMutation.mutate({ email });
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (error) {
            setError('');
        }
    };

    if (submitted) {
        return (
            <div className="min-h-[calc(100vh-9rem)] flex items-center justify-center bg-hx-bg py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md bg-hx-panel border border-hx-line rounded-2xl p-6 sm:p-8 shadow-2xl w-full space-y-8">
                    <div className="text-center">
                        <div className="flex justify-center">
                            <div className="bg-hx-ok/15 p-3 rounded-full">
                                <CheckCircle className="h-12 w-12 text-hx-ok" />
                            </div>
                        </div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-hx-text">
                            Check your email
                        </h2>
                        <p className="mt-2 text-center text-sm text-hx-muted">
                            If an account exists with <span className="font-medium">{email}</span>, we've sent password reset instructions.
                        </p>
                        <p className="mt-4 text-center text-sm text-hx-muted">
                            Didn't receive an email? Check your spam folder or try again in a few minutes.
                        </p>
                    </div>

                    <div className="mt-8 space-y-4">
                        <button
                            onClick={() => {
                                setSubmitted(false);
                                setEmail('');
                            }}
                            className="w-full flex justify-center py-2 px-4 border border-hx-line2 rounded-md shadow-sm text-sm font-medium text-hx-text bg-hx-panel2 hover:bg-hx-line focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-hx-panel focus:ring-hx-accent"
                        >
                            Send another email
                        </button>

                        <Link
                            to="/login"
                            className="w-full flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-hx-ink bg-hx-accent hover:bg-hx-accent2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-hx-panel focus:ring-hx-accent"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to login
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-9rem)] flex items-center justify-center bg-hx-bg py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md bg-hx-panel border border-hx-line rounded-2xl p-6 sm:p-8 shadow-2xl w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-hx-text">
                        Forgot your password?
                    </h2>
                    <p className="mt-2 text-center text-sm text-hx-muted">
                        Enter your email address and we'll send you a link to reset your password.
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-hx-muted mb-1">
                            Email address
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-20">
                                <Mail className={`h-5 w-5 ${error ? 'text-red-400' : 'text-hx-dim'}`} />
                            </div>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className={`appearance-none bg-hx-bg2 relative block w-full pl-10 px-3 py-2 border ${
                                    error ? 'border-red-500/60' : 'border-hx-line2'
                                } placeholder-hx-dim text-hx-text rounded-md focus:outline-none focus:ring-hx-accent focus:border-hx-accent focus:z-10 sm:text-sm`}
                                placeholder="Enter your email"
                                value={email}
                                onChange={handleEmailChange}
                                autoFocus
                            />
                        </div>
                        {error && (
                            <div className="mt-2 flex items-start">
                                <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 mr-1 flex-shrink-0" />
                                <p className="text-sm text-red-400">{error}</p>
                            </div>
                        )}
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={forgotPasswordMutation.isPending}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-hx-ink bg-hx-accent hover:bg-hx-accent2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-hx-panel focus:ring-hx-accent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {forgotPasswordMutation.isPending ? (
                                <>
                                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                    Sending...
                                </>
                            ) : (
                                'Send reset link'
                            )}
                        </button>
                    </div>

                    <div className="text-center space-y-2">
                        <Link
                            to="/login"
                            className="text-sm text-hx-accent hover:text-hx-accent2 flex items-center justify-center"
                        >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Back to login
                        </Link>
                        <p className="text-xs text-hx-muted">
                            Admin? <Link to="/admin/login" className="text-hx-accent hover:text-hx-accent2">Sign in here</Link>
                        </p>
                    </div>
                </form>

                <div className="mt-6 p-4 bg-hx-accent/10 border border-hx-accent/30 rounded-md">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <AlertCircle className="h-5 w-5 text-hx-accent" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-hx-text">
                                Password reset tips
                            </h3>
                            <div className="mt-2 text-sm text-hx-muted">
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Check your spam folder if you don't see the email</li>
                                    <li>Reset links expire after 1 hour</li>
                                    <li>You can request a new link after 60 minutes</li>
                                    <li>Contact support if you continue having issues</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;